import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Database,
  ArrowLeft,
  Save,
  Loader2,
  Users,
  Eye,
  Palette,
  Link as LinkIcon,
  Copy,
  Trash2,
  Plus,
  Mail,
  FileText,
  GripVertical,
  Table,
  Settings,
  ChevronUp,
  ChevronDown,
  ChevronsUp,
  ChevronsDown,
  ChevronRight,
  Pencil,
  EyeOff,
  Search,
  ExternalLink,
  Lock,
  Key,
  Shield,
  UserCheck,
  Filter,
  PlusCircle,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function PortalConfig() {
  const { portalId } = useParams();
  const navigate = useNavigate();
  const { token, csrfToken } = useAuth();
  const [portal, setPortal] = useState(null);
  const [pages, setPages] = useState([]);
  const [schema, setSchema] = useState(null);
  const [clients, setClients] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false); // For Airtable sync
  
  // Page Dialog
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [pageForm, setPageForm] = useState({
    name: "",
    nav_title: "",
    table_name: "",
    description: "",
    airtable_view: "",
    visible_fields: null  // Will be set from view metadata
  });
  const [savingPage, setSavingPage] = useState(false);
  const [loadingViewMetadata, setLoadingViewMetadata] = useState(false);
  
  // Client Dialog
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientRole, setNewClientRole] = useState("");
  const [addingClient, setAddingClient] = useState(false);
  
  // Field config for selected page
  const [selectedPageForFields, setSelectedPageForFields] = useState(null);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [fieldOrder, setFieldOrder] = useState([]);
  const [draggedField, setDraggedField] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [fieldConfig, setFieldConfig] = useState({}); // Per-field settings like allow_create_linked
  const [fieldSearchTerm, setFieldSearchTerm] = useState(""); // Search filter for fields
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null); // For keyboard navigation

  // Nested linked record field configuration
  const [linkedConfigDialogOpen, setLinkedConfigDialogOpen] = useState(false);
  const [linkedConfigFieldName, setLinkedConfigFieldName] = useState(null);
  const [linkedConfigTableId, setLinkedConfigTableId] = useState(null);
  const [linkedConfigTab, setLinkedConfigTab] = useState('preview'); // 'preview', 'expand', or 'create'
  const [linkedExpandConfig, setLinkedExpandConfig] = useState({ visible_fields: [], field_order: [], field_settings: {} });
  const [linkedCreateConfig, setLinkedCreateConfig] = useState({ visible_fields: [], field_order: [], field_settings: {} });
  const [linkedPreviewFields, setLinkedPreviewFields] = useState([]); // Up to 4 fields for preview cards
  const [linkedDraggedField, setLinkedDraggedField] = useState(null);
  const [linkedDragOverIndex, setLinkedDragOverIndex] = useState(null);
  const [linkedFieldSearchTerm, setLinkedFieldSearchTerm] = useState("");

  // Create Form Configuration (page-level)
  const [createFormDialogOpen, setCreateFormDialogOpen] = useState(false);
  const [selectedPageForCreateForm, setSelectedPageForCreateForm] = useState(null);
  const [createFormFields, setCreateFormFields] = useState([]); // Fields visible in create form
  const [createFormFieldOrder, setCreateFormFieldOrder] = useState([]); // Order of fields
  const [createFormFieldSettings, setCreateFormFieldSettings] = useState({}); // Field settings (required, etc.)
  const [createFormDraggedField, setCreateFormDraggedField] = useState(null);
  const [createFormDragOverIndex, setCreateFormDragOverIndex] = useState(null);

  // User Settings & Authentication
  const [userSettingsTab, setUserSettingsTab] = useState('users'); // 'users' or 'auth'
  const [userSettings, setUserSettings] = useState({
    users_table: '',
    role_field: '',
    email_field: '',
    password_field: '',
    auth_method: 'email_password'
  });
  const [savingUserSettings, setSavingUserSettings] = useState(false);

  // Page role access dialog
  const [roleAccessDialogOpen, setRoleAccessDialogOpen] = useState(false);
  const [selectedPageForRoles, setSelectedPageForRoles] = useState(null);
  const [pageAllowedRoles, setPageAllowedRoles] = useState([]);

  const authHeaders = {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };

  // Headers including CSRF token for state-changing requests
  const authHeadersWithCsrf = {
    headers: { 
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
    }
  };

  useEffect(() => {
    fetchData();
  }, [portalId]);

  // Initialize user settings from portal
  useEffect(() => {
    if (portal?.user_settings) {
      setUserSettings({
        users_table: portal.user_settings.users_table || '',
        role_field: portal.user_settings.role_field || '',
        email_field: portal.user_settings.email_field || '',
        password_field: portal.user_settings.password_field || '',
        auth_method: portal.user_settings.auth_method || 'email_password'
      });
    }
  }, [portal]);

  const fetchData = async () => {
    try {
      const [portalRes, schemaRes, clientsRes, invitesRes] = await Promise.all([
        axios.get(`${API}/portals/${portalId}`, authHeadersWithCsrf),
        axios.get(`${API}/airtable/schema?portal_id=${portalId}`, authHeadersWithCsrf),
        axios.get(`${API}/portals/${portalId}/clients`, authHeadersWithCsrf),
        axios.get(`${API}/portals/${portalId}/invites`, authHeaders).catch(() => ({ data: { invites: [] } }))
      ]);
      setPortal(portalRes.data);
      setPages(portalRes.data.pages || []);
      setSchema(schemaRes.data);
      setClients(clientsRes.data.clients || []);
      setInvites(invitesRes.data.invites || []);
    } catch (error) {
      toast.error("Failed to load portal configuration");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePortal = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/portals/${portalId}`, {
        name: portal.name,
        description: portal.description,
        branding: portal.branding,
        is_public: portal.is_public
      }, authHeadersWithCsrf);
      toast.success("Portal saved successfully!");
    } catch (error) {
      toast.error("Failed to save portal");
    } finally {
      setSaving(false);
    }
  };

  // Sync Airtable data - forces fresh fetch of schema and clears caches
  const handleSyncAirtable = async () => {
    setSyncing(true);
    try {
      // Force refresh schema from Airtable (backend will fetch fresh data)
      const schemaRes = await axios.get(`${API}/airtable/schema?portal_id=${portalId}&refresh=true`, authHeadersWithCsrf);
      setSchema(schemaRes.data);
      
      toast.success("Airtable data synced successfully! Schema and table structure updated.");
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Failed to sync Airtable data");
    } finally {
      setSyncing(false);
    }
  };

  // Page Management
  const openAddPage = () => {
    setEditingPage(null);
    setPageForm({ name: "", nav_title: "", table_name: "", description: "", airtable_view: "", visible_fields: null });
    setPageDialogOpen(true);
  };

  const openEditPage = (page) => {
    setEditingPage(page);
    setPageForm({
      name: page.name,
      nav_title: page.nav_title || page.name,
      table_name: page.table_name,
      description: page.description || "",
      airtable_view: page.airtable_view || "",
      visible_fields: page.visible_fields || null
    });
    setPageDialogOpen(true);
  };

  // Fetch view metadata when a view is selected and auto-populate visible_fields
  const fetchViewMetadata = async (tableName, viewName) => {
    console.log('[fetchViewMetadata] Called with:', { tableName, viewName, portalId });
    if (!tableName || !viewName) return;
    
    setLoadingViewMetadata(true);
    try {
      const url = `${API}/airtable/view-metadata?table_name=${encodeURIComponent(tableName)}&view_name=${encodeURIComponent(viewName)}&portal_id=${portalId}`;
      console.log('[fetchViewMetadata] Fetching:', url);
      const response = await axios.get(url, authHeaders);
      console.log('[fetchViewMetadata] Response:', response.data);
      
      if (response.data.visibleFields && response.data.visibleFields.length > 0) {
        setPageForm(prev => ({
          ...prev,
          visible_fields: response.data.visibleFields
        }));
        
        // Show different message based on source
        if (response.data.source === "airtable_view_metadata") {
          toast.success(`Imported ${response.data.visibleFields.length} fields from view "${viewName}" (exact view settings)`);
        } else if (response.data.source === "sample_record_order") {
          toast.success(`Imported ${response.data.visibleFields.length} fields in view order from "${viewName}"`);
        } else {
          toast.info(`Loaded ${response.data.visibleFields.length} fields from table schema.`);
        }
      } else {
        console.log('[fetchViewMetadata] No visible fields in response');
      }
    } catch (error) {
      console.error("[fetchViewMetadata] Error:", error.response?.data || error.message);
      toast.error("Failed to load field settings: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingViewMetadata(false);
    }
  };

  const handleSavePage = async () => {
    if (!pageForm.name || !pageForm.table_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSavingPage(true);
    try {
      if (editingPage) {
        const response = await axios.put(
          `${API}/portals/${portalId}/pages/${editingPage.page_id}`,
          pageForm,
          authHeadersWithCsrf
        );
        setPages(pages.map(p => p.page_id === editingPage.page_id ? response.data : p));
        toast.success("Page updated!");
      } else {
        const response = await axios.post(
          `${API}/portals/${portalId}/pages`,
          pageForm,
          authHeadersWithCsrf
        );
        setPages([...pages, response.data]);
        toast.success("Page created!");
      }
      setPageDialogOpen(false);
    } catch (error) {
      toast.error("Failed to save page");
    } finally {
      setSavingPage(false);
    }
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm("Are you sure you want to delete this page?")) return;
    try {
      await axios.delete(`${API}/portals/${portalId}/pages/${pageId}`, authHeadersWithCsrf);
      setPages(pages.filter(p => p.page_id !== pageId));
      toast.success("Page deleted");
    } catch (error) {
      toast.error("Failed to delete page");
    }
  };

  const movePage = async (index, direction) => {
    const newPages = [...pages];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= pages.length) return;
    
    [newPages[index], newPages[newIndex]] = [newPages[newIndex], newPages[index]];
    setPages(newPages);
    
    try {
      await axios.put(
        `${API}/portals/${portalId}/pages/reorder`,
        { page_ids: newPages.map(p => p.page_id) },
        authHeaders
      );
    } catch (error) {
      toast.error("Failed to reorder pages");
      fetchData();
    }
  };

  // Field visibility for a page
  const getTableFields = (tableName) => {
    return schema?.tables?.find(t => t.name === tableName)?.fields || [];
  };

  // Get field schema by name
  const getFieldSchema = (tableName, fieldName) => {
    const fields = getTableFields(tableName);
    return fields.find(f => f.name === fieldName);
  };

  // Toggle allow_create_linked for a linked record field
  const toggleAllowCreateLinked = async (fieldName) => {
    if (!selectedPageForFields) return;
    
    const currentConfig = fieldConfig[fieldName] || {};
    const newValue = !currentConfig.allow_create_linked;
    
    const newFieldConfig = {
      ...fieldConfig,
      [fieldName]: {
        ...currentConfig,
        allow_create_linked: newValue
      }
    };
    
    setFieldConfig(newFieldConfig);
    
    // Save to backend
    try {
      await axios.put(
        `${API}/portals/${portalId}/pages/${selectedPageForFields.page_id}`,
        { field_config: newFieldConfig },
        authHeaders
      );
      // Update local state
      setSelectedPageForFields({ ...selectedPageForFields, field_config: newFieldConfig });
      setPages(pages.map(p => 
        p.page_id === selectedPageForFields.page_id 
          ? { ...p, field_config: newFieldConfig }
          : p
      ));
      toast.success(`${newValue ? 'Enabled' : 'Disabled'} creating new records for ${fieldName}`);
    } catch (error) {
      toast.error("Failed to update field configuration");
      setFieldConfig(fieldConfig); // Revert
    }
  };

  // Toggle allow_edit for a field
  const toggleAllowEdit = async (fieldName) => {
    if (!selectedPageForFields) return;
    
    const currentConfig = fieldConfig[fieldName] || {};
    const newValue = !currentConfig.allow_edit;
    
    const newFieldConfig = {
      ...fieldConfig,
      [fieldName]: {
        ...currentConfig,
        allow_edit: newValue
      }
    };
    
    setFieldConfig(newFieldConfig);
    
    // Save to backend
    try {
      await axios.put(
        `${API}/portals/${portalId}/pages/${selectedPageForFields.page_id}`,
        { field_config: newFieldConfig },
        authHeaders
      );
      // Update local state
      setSelectedPageForFields({ ...selectedPageForFields, field_config: newFieldConfig });
      setPages(pages.map(p => 
        p.page_id === selectedPageForFields.page_id 
          ? { ...p, field_config: newFieldConfig }
          : p
      ));
      toast.success(`${newValue ? 'Enabled' : 'Disabled'} editing for ${fieldName}`);
    } catch (error) {
      toast.error("Failed to update field configuration");
      setFieldConfig(fieldConfig); // Revert
    }
  };

  // Check if a field type is auto-readonly (formula, rollup, lookup, etc.)
  const isAutoReadonlyField = (fieldType) => {
    const readonlyTypes = ['autoNumber', 'formula', 'rollup', 'lookup', 'count', 'createdTime', 'lastModifiedTime', 'createdBy', 'lastModifiedBy'];
    return readonlyTypes.includes(fieldType);
  };

  // Toggle allow_expand for linked record fields (expandable preview)
  const toggleAllowExpand = async (fieldName) => {
    if (!selectedPageForFields) return;
    
    const currentConfig = fieldConfig[fieldName] || {};
    const newValue = !currentConfig.allow_expand;
    
    const newFieldConfig = {
      ...fieldConfig,
      [fieldName]: {
        ...currentConfig,
        allow_expand: newValue
      }
    };
    
    setFieldConfig(newFieldConfig);
    
    try {
      await axios.put(
        `${API}/portals/${portalId}/pages/${selectedPageForFields.page_id}`,
        { field_config: newFieldConfig },
        authHeaders
      );
      setSelectedPageForFields({ ...selectedPageForFields, field_config: newFieldConfig });
      setPages(pages.map(p => 
        p.page_id === selectedPageForFields.page_id 
          ? { ...p, field_config: newFieldConfig }
          : p
      ));
      toast.success(`${newValue ? 'Enabled' : 'Disabled'} expandable preview for ${fieldName}`);
    } catch (error) {
      toast.error("Failed to update field configuration");
      setFieldConfig(fieldConfig);
    }
  };

  // Toggle allow_create_view for linked record fields (+CREATE button in view mode)
  const toggleAllowCreateView = async (fieldName) => {
    if (!selectedPageForFields) return;
    
    const currentConfig = fieldConfig[fieldName] || {};
    const newValue = !currentConfig.allow_create_view;
    
    const newFieldConfig = {
      ...fieldConfig,
      [fieldName]: {
        ...currentConfig,
        allow_create_view: newValue
      }
    };
    
    setFieldConfig(newFieldConfig);
    
    try {
      await axios.put(
        `${API}/portals/${portalId}/pages/${selectedPageForFields.page_id}`,
        { field_config: newFieldConfig },
        authHeaders
      );
      setSelectedPageForFields({ ...selectedPageForFields, field_config: newFieldConfig });
      setPages(pages.map(p => 
        p.page_id === selectedPageForFields.page_id 
          ? { ...p, field_config: newFieldConfig }
          : p
      ));
      toast.success(`${newValue ? 'Enabled' : 'Disabled'} +CREATE button for ${fieldName}`);
    } catch (error) {
      toast.error("Failed to update field configuration");
      setFieldConfig(fieldConfig);
    }
  };

  // Open linked record field configuration dialog
  const openLinkedFieldConfig = (fieldName, linkedTableId) => {
    if (!linkedTableId) {
      toast.error("Could not find linked table");
      return;
    }
    
    setLinkedConfigFieldName(fieldName);
    setLinkedConfigTableId(linkedTableId);
    setLinkedConfigTab('preview'); // Default to preview tab
    setLinkedFieldSearchTerm("");
    
    // Get linked table fields
    const linkedTable = schema?.tables?.find(t => t.id === linkedTableId);
    const linkedFields = linkedTable?.fields || [];
    const allFieldNames = linkedFields.map(f => f.name);
    
    // Load existing config or initialize with all fields visible
    const currentConfig = fieldConfig[fieldName] || {};
    
    // Initialize preview fields (up to 4 fields for preview cards)
    const existingPreviewFields = currentConfig.preview_fields || [];
    // Default to first 3 non-link fields if not configured
    const defaultPreviewFields = existingPreviewFields.length > 0 
      ? existingPreviewFields 
      : linkedFields
          .filter(f => f.type !== 'multipleRecordLinks')
          .slice(0, 3)
          .map(f => f.name);
    setLinkedPreviewFields(defaultPreviewFields);
    
    // Initialize expand config
    const existingExpandConfig = currentConfig.expand_config || {};
    setLinkedExpandConfig({
      visible_fields: existingExpandConfig.visible_fields || [...allFieldNames],
      field_order: existingExpandConfig.field_order || [...allFieldNames],
      field_settings: existingExpandConfig.field_settings || {}
    });
    
    // Initialize create config
    const existingCreateConfig = currentConfig.create_config || {};
    // For create, default to editable fields only (exclude formula, rollup, etc.)
    const editableFields = linkedFields
      .filter(f => !isAutoReadonlyField(f.type))
      .map(f => f.name);
    setLinkedCreateConfig({
      visible_fields: existingCreateConfig.visible_fields || [...editableFields],
      field_order: existingCreateConfig.field_order || [...editableFields],
      field_settings: existingCreateConfig.field_settings || {}
    });
    
    setLinkedConfigDialogOpen(true);
  };

  // Get linked table by ID
  const getLinkedTableById = (tableId) => {
    return schema?.tables?.find(t => t.id === tableId);
  };

  // Get fields for linked table
  const getLinkedTableFields = (tableId) => {
    const table = getLinkedTableById(tableId);
    return table?.fields || [];
  };
  
  // Toggle a field in preview fields list
  const togglePreviewField = (fieldName) => {
    if (linkedPreviewFields.includes(fieldName)) {
      setLinkedPreviewFields(linkedPreviewFields.filter(f => f !== fieldName));
    } else if (linkedPreviewFields.length < 4) {
      setLinkedPreviewFields([...linkedPreviewFields, fieldName]);
    } else {
      toast.error("Maximum 4 preview fields allowed");
    }
  };
  
  // Move preview field up or down
  const movePreviewField = (index, direction) => {
    const newFields = [...linkedPreviewFields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newFields.length) return;
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setLinkedPreviewFields(newFields);
  };

  // Check if field is visible in linked config (for current tab)
  const isLinkedFieldVisible = (fieldName) => {
    const config = linkedConfigTab === 'expand' ? linkedExpandConfig : linkedCreateConfig;
    return config.visible_fields?.includes(fieldName) ?? true;
  };

  // Toggle linked field visibility
  const toggleLinkedFieldVisibility = (fieldName) => {
    if (linkedConfigTab === 'expand') {
      const newVisibleFields = linkedExpandConfig.visible_fields.includes(fieldName)
        ? linkedExpandConfig.visible_fields.filter(f => f !== fieldName)
        : [...linkedExpandConfig.visible_fields, fieldName];
      // Also ensure field is in field_order
      const newFieldOrder = linkedExpandConfig.field_order.includes(fieldName)
        ? linkedExpandConfig.field_order
        : [...linkedExpandConfig.field_order, fieldName];
      setLinkedExpandConfig({ ...linkedExpandConfig, visible_fields: newVisibleFields, field_order: newFieldOrder });
    } else {
      const newVisibleFields = linkedCreateConfig.visible_fields.includes(fieldName)
        ? linkedCreateConfig.visible_fields.filter(f => f !== fieldName)
        : [...linkedCreateConfig.visible_fields, fieldName];
      // Also ensure field is in field_order
      const newFieldOrder = linkedCreateConfig.field_order.includes(fieldName)
        ? linkedCreateConfig.field_order
        : [...linkedCreateConfig.field_order, fieldName];
      setLinkedCreateConfig({ ...linkedCreateConfig, visible_fields: newVisibleFields, field_order: newFieldOrder });
    }
  };

  // Get linked field editable setting
  const isLinkedFieldEditable = (fieldName) => {
    const config = linkedConfigTab === 'expand' ? linkedExpandConfig : linkedCreateConfig;
    return config.field_settings?.[fieldName]?.allow_edit ?? false;
  };

  // Toggle linked field editable
  const toggleLinkedFieldEditable = (fieldName) => {
    if (linkedConfigTab === 'expand') {
      const currentSetting = linkedExpandConfig.field_settings?.[fieldName]?.allow_edit ?? false;
      setLinkedExpandConfig({
        ...linkedExpandConfig,
        field_settings: {
          ...linkedExpandConfig.field_settings,
          [fieldName]: { ...(linkedExpandConfig.field_settings?.[fieldName] || {}), allow_edit: !currentSetting }
        }
      });
    } else {
      const currentSetting = linkedCreateConfig.field_settings?.[fieldName]?.allow_edit ?? false;
      setLinkedCreateConfig({
        ...linkedCreateConfig,
        field_settings: {
          ...linkedCreateConfig.field_settings,
          [fieldName]: { ...(linkedCreateConfig.field_settings?.[fieldName] || {}), allow_edit: !currentSetting }
        }
      });
    }
  };

  // Get linked field required setting (only for create form)
  const isLinkedFieldRequired = (fieldName) => {
    return linkedCreateConfig.field_settings?.[fieldName]?.required ?? false;
  };

  // Toggle linked field required (only for create form)
  const toggleLinkedFieldRequired = (fieldName) => {
    const currentSetting = linkedCreateConfig.field_settings?.[fieldName]?.required ?? false;
    setLinkedCreateConfig({
      ...linkedCreateConfig,
      field_settings: {
        ...linkedCreateConfig.field_settings,
        [fieldName]: { ...(linkedCreateConfig.field_settings?.[fieldName] || {}), required: !currentSetting }
      }
    });
  };

  // Handle drag start for linked fields
  const handleLinkedDragStart = (e, fieldName, index) => {
    setLinkedDraggedField({ name: fieldName, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over for linked fields
  const handleLinkedDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setLinkedDragOverIndex(index);
  };

  // Handle drag leave for linked fields
  const handleLinkedDragLeave = () => {
    setLinkedDragOverIndex(null);
  };

  // Handle drop for linked fields
  const handleLinkedDrop = (e, dropIndex) => {
    e.preventDefault();
    if (!linkedDraggedField) return;
    
    const config = linkedConfigTab === 'expand' ? linkedExpandConfig : linkedCreateConfig;
    const setConfig = linkedConfigTab === 'expand' ? setLinkedExpandConfig : setLinkedCreateConfig;
    
    const currentOrder = [...config.field_order];
    const dragIndex = currentOrder.indexOf(linkedDraggedField.name);
    
    if (dragIndex === -1 || dragIndex === dropIndex) {
      setLinkedDraggedField(null);
      setLinkedDragOverIndex(null);
      return;
    }
    
    // Remove from old position and insert at new position
    currentOrder.splice(dragIndex, 1);
    currentOrder.splice(dropIndex, 0, linkedDraggedField.name);
    
    setConfig({ ...config, field_order: currentOrder });
    setLinkedDraggedField(null);
    setLinkedDragOverIndex(null);
  };

  // Handle drag end for linked fields
  const handleLinkedDragEnd = () => {
    setLinkedDraggedField(null);
    setLinkedDragOverIndex(null);
  };

  // Move linked field up or down
  const moveLinkedField = (index, direction) => {
    const config = linkedConfigTab === 'expand' ? linkedExpandConfig : linkedCreateConfig;
    const setConfig = linkedConfigTab === 'expand' ? setLinkedExpandConfig : setLinkedCreateConfig;
    
    const newOrder = [...config.field_order];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newOrder.length) return;
    
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    setConfig({ ...config, field_order: newOrder });
  };

  // Move linked field to top
  const moveLinkedFieldToTop = (index) => {
    const config = linkedConfigTab === 'expand' ? linkedExpandConfig : linkedCreateConfig;
    const setConfig = linkedConfigTab === 'expand' ? setLinkedExpandConfig : setLinkedCreateConfig;
    
    if (index === 0) return;
    const newOrder = [...config.field_order];
    const [removed] = newOrder.splice(index, 1);
    newOrder.unshift(removed);
    setConfig({ ...config, field_order: newOrder });
  };

  // Move linked field to bottom
  const moveLinkedFieldToBottom = (index) => {
    const config = linkedConfigTab === 'expand' ? linkedExpandConfig : linkedCreateConfig;
    const setConfig = linkedConfigTab === 'expand' ? setLinkedExpandConfig : setLinkedCreateConfig;
    
    if (index === config.field_order.length - 1) return;
    const newOrder = [...config.field_order];
    const [removed] = newOrder.splice(index, 1);
    newOrder.push(removed);
    setConfig({ ...config, field_order: newOrder });
  };

  // Bulk visibility toggles for linked fields
  const showAllLinkedFields = () => {
    // Use functional update to avoid stale closure issues
    if (linkedConfigTab === 'expand') {
      setLinkedExpandConfig(prev => ({ ...prev, visible_fields: [...prev.field_order] }));
    } else {
      setLinkedCreateConfig(prev => ({ ...prev, visible_fields: [...prev.field_order] }));
    }
    toast.success("All fields are now visible");
  };

  const hideAllLinkedFields = () => {
    // Use functional update to avoid stale closure issues
    if (linkedConfigTab === 'expand') {
      setLinkedExpandConfig(prev => ({ ...prev, visible_fields: [] }));
    } else {
      setLinkedCreateConfig(prev => ({ ...prev, visible_fields: [] }));
    }
    toast.success("All fields are now hidden");
  };

  // Bulk editable toggles for linked fields
  const makeAllLinkedEditable = () => {
    const linkedFields = getLinkedTableFields(linkedConfigTableId);
    
    // Build new field_settings with all editable fields set to allow_edit: true
    const buildNewSettings = (prevSettings) => {
      const newFieldSettings = { ...(prevSettings || {}) };
      linkedFields.forEach(f => {
        if (!isAutoReadonlyField(f.type)) {
          newFieldSettings[f.name] = { ...(newFieldSettings[f.name] || {}), allow_edit: true };
        }
      });
      return newFieldSettings;
    };
    
    // Use functional update to avoid stale closure issues
    if (linkedConfigTab === 'expand') {
      setLinkedExpandConfig(prev => ({ ...prev, field_settings: buildNewSettings(prev.field_settings) }));
    } else {
      setLinkedCreateConfig(prev => ({ ...prev, field_settings: buildNewSettings(prev.field_settings) }));
    }
    toast.success("All editable fields are now editable");
  };

  const makeNoneLinkedEditable = () => {
    const linkedFields = getLinkedTableFields(linkedConfigTableId);
    
    // Build new field_settings with all fields set to allow_edit: false
    const buildNewSettings = (prevSettings) => {
      const newFieldSettings = { ...(prevSettings || {}) };
      linkedFields.forEach(f => {
        newFieldSettings[f.name] = { ...(newFieldSettings[f.name] || {}), allow_edit: false };
      });
      return newFieldSettings;
    };
    
    // Use functional update to avoid stale closure issues
    if (linkedConfigTab === 'expand') {
      setLinkedExpandConfig(prev => ({ ...prev, field_settings: buildNewSettings(prev.field_settings) }));
    } else {
      setLinkedCreateConfig(prev => ({ ...prev, field_settings: buildNewSettings(prev.field_settings) }));
    }
    toast.success("All fields are now read-only");
  };

  // Save linked field configuration
  const saveLinkedFieldConfig = async () => {
    if (!selectedPageForFields || !linkedConfigFieldName) return;
    
    // Ensure field_order includes all fields from the linked table
    const linkedFields = getLinkedTableFields(linkedConfigTableId);
    const allFieldNames = linkedFields.map(f => f.name);
    
    // Sync field_order: keep existing order, add any missing fields
    const syncFieldOrder = (config) => {
      const existingOrder = config.field_order || [];
      const existingSet = new Set(existingOrder);
      const completeOrder = [
        ...existingOrder.filter(name => allFieldNames.includes(name)),
        ...allFieldNames.filter(name => !existingSet.has(name))
      ];
      return { ...config, field_order: completeOrder };
    };
    
    const syncedExpandConfig = syncFieldOrder(linkedExpandConfig);
    const syncedCreateConfig = syncFieldOrder(linkedCreateConfig);
    
    const currentConfig = fieldConfig[linkedConfigFieldName] || {};
    const newFieldConfig = {
      ...fieldConfig,
      [linkedConfigFieldName]: {
        ...currentConfig,
        preview_fields: linkedPreviewFields, // Save preview fields
        expand_config: syncedExpandConfig,
        create_config: syncedCreateConfig
      }
    };
    
    setFieldConfig(newFieldConfig);
    
    try {
      await axios.put(
        `${API}/portals/${portalId}/pages/${selectedPageForFields.page_id}`,
        { field_config: newFieldConfig },
        authHeaders
      );
      setSelectedPageForFields({ ...selectedPageForFields, field_config: newFieldConfig });
      setPages(pages.map(p => 
        p.page_id === selectedPageForFields.page_id 
          ? { ...p, field_config: newFieldConfig }
          : p
      ));
      toast.success(`Saved configuration for ${linkedConfigFieldName}`);
      setLinkedConfigDialogOpen(false);
    } catch (error) {
      toast.error("Failed to save linked field configuration");
    }
  };

  // Get filtered linked field order for current tab
  // This should include ALL fields from the linked table, not just the saved ones
  const getFilteredLinkedFieldOrder = () => {
    const config = linkedConfigTab === 'expand' ? linkedExpandConfig : linkedCreateConfig;
    const savedFieldOrder = config.field_order || [];
    
    // Get all fields from the linked table
    const linkedFields = getLinkedTableFields(linkedConfigTableId);
    const allFieldNames = linkedFields.map(f => f.name);
    
    // Create a complete field order: saved fields first, then any new fields
    const savedSet = new Set(savedFieldOrder);
    const completeFieldOrder = [
      ...savedFieldOrder.filter(name => allFieldNames.includes(name)), // Saved fields that still exist
      ...allFieldNames.filter(name => !savedSet.has(name)) // New fields not in saved config
    ];
    
    if (!linkedFieldSearchTerm) return completeFieldOrder;
    
    return completeFieldOrder.filter(fieldName => 
      fieldName.toLowerCase().includes(linkedFieldSearchTerm.toLowerCase())
    );
  };

  const openFieldConfig = (page) => {
    setSelectedPageForFields(page);
    const tableFields = getTableFields(page.table_name);
    const schemaFieldNames = new Set(tableFields.map(f => f.name));
    
    // Initialize field order based on visible_fields or all fields
    if (page.visible_fields && page.visible_fields.length > 0) {
      // Filter out stale field names that no longer exist in schema
      const validVisibleFields = page.visible_fields.filter(f => schemaFieldNames.has(f));
      const visibleSet = new Set(validVisibleFields);
      
      // Start with valid visible fields in order, then add hidden ones from schema
      const orderedFields = [
        ...validVisibleFields,
        ...tableFields.filter(f => !visibleSet.has(f.name)).map(f => f.name)
      ];
      setFieldOrder(orderedFields);
    } else {
      setFieldOrder(tableFields.map(f => f.name));
    }
    // Load field config (per-field settings)
    setFieldConfig(page.field_config || {});
    setFieldDialogOpen(true);
  };

  const isFieldVisible = (fieldName) => {
    if (!selectedPageForFields) return true;
    // If visible_fields is null or undefined, all are visible by default
    // If visible_fields is an empty array [], none are visible (explicitly hidden)
    if (selectedPageForFields.visible_fields === null || selectedPageForFields.visible_fields === undefined) {
      return true; // All visible by default when not configured
    }
    if (selectedPageForFields.visible_fields.length === 0) {
      return false; // All hidden when explicitly set to empty array
    }
    // Check if field is in the visible_fields list
    return selectedPageForFields.visible_fields.includes(fieldName);
  };

  const toggleFieldVisibility = async (fieldName) => {
    if (!selectedPageForFields) return;
    
    const currentFields = selectedPageForFields.visible_fields;
    const allFields = getTableFields(selectedPageForFields.table_name).map(f => f.name);
    
    if (allFields.length === 0) {
      toast.error("Schema not loaded. Please try refreshing the page.");
      return;
    }
    
    let newFields;
    // Check if visible_fields is null/undefined (meaning all are visible by default)
    // vs empty array [] (meaning none are visible - explicitly hidden)
    const allVisibleByDefault = currentFields === null || currentFields === undefined;
    
    if (allVisibleByDefault) {
      // All were visible by default, now hide this one field
      newFields = allFields.filter(f => f !== fieldName);
    } else if (currentFields.includes(fieldName)) {
      // Field is currently visible, remove it
      newFields = currentFields.filter(f => f !== fieldName);
    } else {
      // Field is currently hidden, add it
      newFields = [...currentFields, fieldName];
    }
    
    try {
      const response = await axios.put(
        `${API}/portals/${portalId}/pages/${selectedPageForFields.page_id}`,
        { visible_fields: newFields },
        authHeaders
      );
      setPages(pages.map(p => p.page_id === selectedPageForFields.page_id ? response.data : p));
      setSelectedPageForFields(response.data);
      
      // Determine if we just showed or hid the field
      const wasVisible = allVisibleByDefault || currentFields.includes(fieldName);
      toast.success(`${wasVisible ? 'Hidden' : 'Shown'}: ${fieldName}`);
    } catch (error) {
      toast.error("Failed to update field visibility");
    }
  };

  const moveField = (index, direction) => {
    const newOrder = [...fieldOrder];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fieldOrder.length) return;
    
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    setFieldOrder(newOrder);
    setSelectedFieldIndex(newIndex); // Keep selection on the moved field
    
    // Update visible_fields with new order (only visible ones)
    const visibleInOrder = newOrder.filter(f => isFieldVisible(f));
    updateFieldOrder(visibleInOrder);
  };

  // Move field to top of list
  const moveFieldToTop = (index) => {
    if (index === 0) return;
    const newOrder = [...fieldOrder];
    const [removed] = newOrder.splice(index, 1);
    newOrder.unshift(removed);
    setFieldOrder(newOrder);
    setSelectedFieldIndex(0);
    
    const visibleInOrder = newOrder.filter(f => isFieldVisible(f));
    updateFieldOrder(visibleInOrder);
  };

  // Move field to bottom of list
  const moveFieldToBottom = (index) => {
    if (index === fieldOrder.length - 1) return;
    const newOrder = [...fieldOrder];
    const [removed] = newOrder.splice(index, 1);
    newOrder.push(removed);
    setFieldOrder(newOrder);
    setSelectedFieldIndex(newOrder.length - 1);
    
    const visibleInOrder = newOrder.filter(f => isFieldVisible(f));
    updateFieldOrder(visibleInOrder);
  };

  // Bulk visibility toggles
  const showAllFields = () => {
    const allFields = fieldOrder;
    const visibleInOrder = allFields;
    
    // Update all fields to be visible
    setPages(pages.map(p => {
      if (p.page_id === selectedPageForFields?.page_id) {
        return { ...p, visible_fields: visibleInOrder };
      }
      return p;
    }));
    setSelectedPageForFields(prev => prev ? { ...prev, visible_fields: visibleInOrder } : null);
    updateFieldOrder(visibleInOrder);
    toast.success(`All ${allFields.length} fields are now visible`);
  };

  const hideAllFields = () => {
    const updatedPages = pages.map(p => {
      if (p.page_id === selectedPageForFields?.page_id) {
        return { ...p, visible_fields: [] };
      }
      return p;
    });
    setPages(updatedPages);
    setSelectedPageForFields(prev => prev ? { ...prev, visible_fields: [] } : null);
    updateFieldOrder([]);
    toast.success("All fields are now hidden");
  };

  // Bulk editable toggles
  const makeAllEditable = () => {
    const newFieldConfig = { ...fieldConfig };
    fieldOrder.forEach(fieldName => {
      const fieldSchema = selectedPageForFields ? getFieldSchema(selectedPageForFields.table_name, fieldName) : null;
      const fieldType = fieldSchema?.type || 'text';
      const isReadonly = isAutoReadonlyField(fieldType);
      if (!isReadonly) {
        newFieldConfig[fieldName] = { ...newFieldConfig[fieldName], allow_edit: true };
      }
    });
    setFieldConfig(newFieldConfig);
    saveFieldConfig(newFieldConfig);
    toast.success("All editable fields are now editable");
  };

  const makeNoneEditable = () => {
    const newFieldConfig = { ...fieldConfig };
    fieldOrder.forEach(fieldName => {
      newFieldConfig[fieldName] = { ...newFieldConfig[fieldName], allow_edit: false };
    });
    setFieldConfig(newFieldConfig);
    saveFieldConfig(newFieldConfig);
    toast.success("All fields are now read-only");
  };

  // Helper to save field config
  const saveFieldConfig = async (config) => {
    if (!selectedPageForFields) return;
    try {
      await axios.put(
        `${API}/portals/${portalId}/pages/${selectedPageForFields.page_id}`,
        { field_config: config },
        authHeaders
      );
    } catch (error) {
      // Silent fail - will save on next toggle
    }
  };

  // Keyboard navigation for field reordering
  const handleFieldKeyDown = (e, index) => {
    if (e.key === 'ArrowUp' && e.altKey) {
      e.preventDefault();
      moveField(index, 'up');
    } else if (e.key === 'ArrowDown' && e.altKey) {
      e.preventDefault();
      moveField(index, 'down');
    } else if (e.key === 'Home' && e.altKey) {
      e.preventDefault();
      moveFieldToTop(index);
    } else if (e.key === 'End' && e.altKey) {
      e.preventDefault();
      moveFieldToBottom(index);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, fieldName, index) => {
    setDraggedField({ name: fieldName, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fieldName);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (!draggedField || draggedField.index === dropIndex) {
      setDraggedField(null);
      setDragOverIndex(null);
      return;
    }
    
    const newOrder = [...fieldOrder];
    const [removed] = newOrder.splice(draggedField.index, 1);
    newOrder.splice(dropIndex, 0, removed);
    
    setFieldOrder(newOrder);
    setDraggedField(null);
    setDragOverIndex(null);
    
    // Update visible_fields with new order (only visible ones)
    const visibleInOrder = newOrder.filter(f => isFieldVisible(f));
    updateFieldOrder(visibleInOrder);
  };

  const handleDragEnd = () => {
    setDraggedField(null);
    setDragOverIndex(null);
  };

  // Filter fields based on search term
  const filteredFieldOrder = fieldSearchTerm
    ? fieldOrder.filter(fieldName => 
        fieldName.toLowerCase().includes(fieldSearchTerm.toLowerCase())
      )
    : fieldOrder;

  const updateFieldOrder = async (orderedVisibleFields) => {
    if (!selectedPageForFields) return;
    
    try {
      // Send empty array as empty array, not null - we want to explicitly hide all fields
      const response = await axios.put(
        `${API}/portals/${portalId}/pages/${selectedPageForFields.page_id}`,
        { visible_fields: orderedVisibleFields },
        authHeaders
      );
      setPages(pages.map(p => p.page_id === selectedPageForFields.page_id ? response.data : p));
      setSelectedPageForFields(response.data);
    } catch (error) {
      toast.error("Failed to update field order");
    }
  };

  // Client Management
  const handleAddClient = async () => {
    if (!newClientEmail) return;
    setAddingClient(true);
    try {
      const response = await axios.post(`${API}/portals/${portalId}/invites`, {
        email: newClientEmail,
        portal_id: portalId,
        role: newClientRole || null
      }, authHeadersWithCsrf);
      setInvites([response.data.invite, ...invites]);
      setNewClientEmail("");
      setNewClientRole("");
      setClientDialogOpen(false);
      
      // Show appropriate message based on email status
      if (response.data.email_sent) {
        toast.success("Invitation sent to " + newClientEmail);
      } else {
        toast.warning("Invite created but email failed: " + (response.data.email_error || "Unknown error"));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setAddingClient(false);
    }
  };

  const handleResendInvite = async (inviteId, email) => {
    try {
      const response = await axios.post(`${API}/portals/${portalId}/invites/${inviteId}/resend`, {}, authHeadersWithCsrf);
      if (response.data.email_sent) {
        toast.success("Invitation resent to " + email);
        // Refresh invites to get updated data
        fetchData();
      } else {
        toast.error("Failed to send email: " + (response.data.error || "Unknown error"));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invitation");
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/portals/${portalId}/invites/${inviteId}`, authHeadersWithCsrf);
      setInvites(invites.filter(i => i.invite_id !== inviteId));
      toast.success("Invite cancelled");
    } catch (error) {
      toast.error("Failed to cancel invite");
    }
  };

  const fetchInvitesAndClients = async () => {
    try {
      const [invitesRes, clientsRes] = await Promise.all([
        axios.get(`${API}/portals/${portalId}/invites`, authHeaders),
        axios.get(`${API}/portals/${portalId}/clients`, authHeaders)
      ]);
      setInvites(invitesRes.data.invites || []);
      setClients(clientsRes.data.clients || []);
    } catch (error) {
      console.error("Failed to fetch invites/clients:", error);
    }
  };

  const handleRemoveClient = async (accessId) => {
    try {
      await axios.delete(`${API}/portals/${portalId}/clients/${accessId}`, authHeadersWithCsrf);
      setClients(clients.filter(c => c.access_id !== accessId));
      toast.success("Client access removed");
    } catch (error) {
      toast.error("Failed to remove client");
    }
  };

  const copyClientLink = (accessToken) => {
    const url = `${window.location.origin}/c/${accessToken}`;
    navigator.clipboard.writeText(url);
    toast.success("Client link copied!");
  };

  const copyPublicLink = () => {
    const url = `${window.location.origin}/p/${portal.share_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Public link copied!");
  };

  // User Settings & Authentication functions
  const handleSaveUserSettings = async () => {
    setSavingUserSettings(true);
    try {
      await axios.put(`${API}/portals/${portalId}`, {
        user_settings: userSettings
      }, authHeadersWithCsrf);
      setPortal({ ...portal, user_settings: userSettings });
      toast.success("User settings saved!");
    } catch (error) {
      toast.error("Failed to save user settings");
    } finally {
      setSavingUserSettings(false);
    }
  };

  // Get fields from a specific table (for dropdowns)
  const getTableFieldsByName = (tableName) => {
    return schema?.tables?.find(t => t.name === tableName)?.fields || [];
  };

  // Get single select fields from a table (for role field dropdown)
  const getSingleSelectFields = (tableName) => {
    const fields = getTableFieldsByName(tableName);
    return fields.filter(f => f.type === 'singleSelect');
  };

  // Get role options from the selected role field
  const getRoleOptions = () => {
    if (!userSettings.users_table || !userSettings.role_field) return [];
    const fields = getTableFieldsByName(userSettings.users_table);
    const roleField = fields.find(f => f.name === userSettings.role_field);
    return roleField?.options?.choices?.map(c => c.name) || [];
  };

  // Open page role access dialog
  const openRoleAccessDialog = (page) => {
    setSelectedPageForRoles(page);
    setPageAllowedRoles(page.allowed_roles || []);
    setRoleAccessDialogOpen(true);
  };

  // Toggle role access for a page
  const toggleRoleAccess = (role) => {
    if (pageAllowedRoles.includes(role)) {
      setPageAllowedRoles(pageAllowedRoles.filter(r => r !== role));
    } else {
      setPageAllowedRoles([...pageAllowedRoles, role]);
    }
  };

  // Save page role access
  const savePageRoleAccess = async () => {
    if (!selectedPageForRoles) return;
    try {
      const response = await axios.put(
        `${API}/portals/${portalId}/pages/${selectedPageForRoles.page_id}`,
        { allowed_roles: pageAllowedRoles },
        authHeaders
      );
      setPages(pages.map(p => p.page_id === selectedPageForRoles.page_id ? response.data : p));
      setRoleAccessDialogOpen(false);
      toast.success("Page access updated!");
    } catch (error) {
      toast.error("Failed to update page access");
    }
  };

  // Toggle "New Record" button visibility for a page
  const toggleAllowCreate = async (page) => {
    const newValue = !page.allow_create;
    try {
      const response = await axios.put(
        `${API}/portals/${portalId}/pages/${page.page_id}`,
        { allow_create: newValue },
        authHeaders
      );
      setPages(pages.map(p => p.page_id === page.page_id ? response.data : p));
      toast.success(newValue ? "New Record button enabled" : "New Record button hidden");
    } catch (error) {
      toast.error("Failed to update page setting");
    }
  };

  // Open Create Form Configuration dialog
  const openCreateFormConfig = (page) => {
    setSelectedPageForCreateForm(page);
    const existingConfig = page.create_form_config || {};
    setCreateFormFields(existingConfig.visible_fields || []);
    setCreateFormFieldOrder(existingConfig.field_order || []);
    setCreateFormFieldSettings(existingConfig.field_settings || {});
    setCreateFormDialogOpen(true);
  };

  // Toggle field visibility in create form
  const toggleCreateFormField = (fieldName) => {
    if (createFormFields.includes(fieldName)) {
      setCreateFormFields(createFormFields.filter(f => f !== fieldName));
      setCreateFormFieldOrder(createFormFieldOrder.filter(f => f !== fieldName));
      // Also remove field settings when field is removed
      const { [fieldName]: _, ...rest } = createFormFieldSettings;
      setCreateFormFieldSettings(rest);
    } else {
      setCreateFormFields([...createFormFields, fieldName]);
      setCreateFormFieldOrder([...createFormFieldOrder, fieldName]);
    }
  };

  // Toggle required for a field in create form
  const toggleCreateFormRequired = (fieldName) => {
    setCreateFormFieldSettings(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        required: !prev[fieldName]?.required
      }
    }));
  };

  // Handle drag for create form fields
  const handleCreateFormDragStart = (e, index) => {
    setCreateFormDraggedField(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCreateFormDragOver = (e, index) => {
    e.preventDefault();
    setCreateFormDragOverIndex(index);
  };

  const handleCreateFormDrop = (e, dropIndex) => {
    e.preventDefault();
    if (createFormDraggedField === null || createFormDraggedField === dropIndex) {
      setCreateFormDraggedField(null);
      setCreateFormDragOverIndex(null);
      return;
    }

    const newOrder = [...createFormFieldOrder];
    const [removed] = newOrder.splice(createFormDraggedField, 1);
    newOrder.splice(dropIndex, 0, removed);
    setCreateFormFieldOrder(newOrder);
    setCreateFormDraggedField(null);
    setCreateFormDragOverIndex(null);
  };

  // Save create form configuration
  const saveCreateFormConfig = async () => {
    if (!selectedPageForCreateForm) return;
    try {
      const response = await axios.put(
        `${API}/portals/${portalId}/pages/${selectedPageForCreateForm.page_id}`,
        { 
          create_form_config: {
            visible_fields: createFormFields,
            field_order: createFormFieldOrder,
            field_settings: createFormFieldSettings
          }
        },
        authHeaders
      );
      setPages(pages.map(p => p.page_id === selectedPageForCreateForm.page_id ? response.data : p));
      setCreateFormDialogOpen(false);
      toast.success("Create form configuration saved!");
    } catch (error) {
      toast.error("Failed to save create form configuration");
    }
  };

  // Get fields for create form (excluding read-only types)
  const getCreateFormAvailableFields = () => {
    if (!selectedPageForCreateForm || !schema) return [];
    const table = schema.tables?.find(t => t.name === selectedPageForCreateForm.table_name);
    if (!table) return [];
    
    const readOnlyTypes = ['autoNumber', 'formula', 'rollup', 'lookup', 'count', 'createdTime', 'lastModifiedTime', 'createdBy', 'lastModifiedBy'];
    return table.fields?.filter(f => !readOnlyTypes.includes(f.type)) || [];
  };

  // Check if field type is filterable (singleSelect, multipleSelects, checkbox)
  const isFilterableFieldType = (fieldType) => {
    return ['singleSelect', 'multipleSelects', 'checkbox'].includes(fieldType);
  };

  // Get count of filterable fields for current page
  const getFilterableCount = () => {
    if (!fieldConfig) return 0;
    return Object.values(fieldConfig).filter(config => config?.is_filterable).length;
  };

  // Toggle field as filterable
  const toggleFilterable = async (fieldName) => {
    if (!selectedPageForFields) return;
    
    const currentlyFilterable = fieldConfig[fieldName]?.is_filterable || false;
    
    // Check if we're at max and trying to add more
    if (!currentlyFilterable && getFilterableCount() >= 8) {
      toast.error("Maximum 8 filters allowed per page");
      return;
    }
    
    const newFieldConfig = {
      ...fieldConfig,
      [fieldName]: {
        ...fieldConfig[fieldName],
        is_filterable: !currentlyFilterable
      }
    };
    setFieldConfig(newFieldConfig);
    
    // Save to backend
    try {
      await axios.put(
        `${API}/portals/${portalId}/pages/${selectedPageForFields.page_id}`,
        { field_config: newFieldConfig },
        authHeaders
      );
      // Update local state
      setSelectedPageForFields({ ...selectedPageForFields, field_config: newFieldConfig });
      setPages(pages.map(p => 
        p.page_id === selectedPageForFields.page_id 
          ? { ...p, field_config: newFieldConfig }
          : p
      ));
      toast.success(`${!currentlyFilterable ? 'Enabled' : 'Disabled'} filter for ${fieldName}`);
    } catch (error) {
      toast.error("Failed to update field configuration");
      setFieldConfig(fieldConfig); // Revert
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">Portal Settings</h1>
              <p className="text-sm text-muted-foreground">{portal?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button
              variant="outline"
              className="btn-secondary"
              onClick={handleSyncAirtable}
              disabled={syncing}
              data-testid="sync-btn"
              title="Sync Airtable schema and data"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sync
            </Button>
            <Button
              variant="outline"
              className="btn-secondary"
              onClick={() => navigate(`/portal/${portalId}`)}
              data-testid="preview-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              className="btn-primary"
              onClick={handleSavePortal}
              disabled={saving}
              data-testid="save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-8">
        {/* Basic Settings */}
        <section className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Basic Settings</h2>
              <p className="text-sm text-muted-foreground">Configure portal name and description</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-foreground/80">Portal Name</Label>
              <Input
                className="mt-2 bg-background border-border text-foreground focus:border-primary"
                value={portal?.name || ""}
                onChange={(e) => setPortal({ ...portal, name: e.target.value })}
                data-testid="portal-name-input"
              />
            </div>
            <div>
              <Label className="text-foreground/80">Description</Label>
              <Input
                className="mt-2 bg-background border-border text-foreground focus:border-primary"
                value={portal?.description || ""}
                onChange={(e) => setPortal({ ...portal, description: e.target.value })}
                data-testid="portal-description-input"
              />
            </div>
          </div>
        </section>

        {/* Pages Management */}
        <section className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">Pages</h2>
                <p className="text-sm text-muted-foreground">Manage pages and navigation order</p>
              </div>
            </div>
            <Dialog open={pageDialogOpen} onOpenChange={setPageDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" onClick={openAddPage} data-testid="add-page-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Page
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border text-foreground">
                <DialogHeader>
                  <DialogTitle>{editingPage ? "Edit Page" : "Add New Page"}</DialogTitle>
                  <DialogDescription className="text-muted-foreground">
                    Connect an Airtable table to create a page
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-foreground/80">Page Name *</Label>
                    <Input
                      className="mt-2 bg-background border-border text-foreground focus:border-primary"
                      placeholder="Clients"
                      value={pageForm.name}
                      onChange={(e) => setPageForm({ ...pageForm, name: e.target.value })}
                      data-testid="page-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-foreground/80">Navigation Title</Label>
                    <Input
                      className="mt-2 bg-background border-border text-foreground focus:border-primary"
                      placeholder="Same as page name if empty"
                      value={pageForm.nav_title}
                      onChange={(e) => setPageForm({ ...pageForm, nav_title: e.target.value })}
                      data-testid="page-nav-title-input"
                    />
                  </div>
                  <div>
                    <Label className="text-foreground/80">Airtable Table *</Label>
                    {schema?.tables?.length > 0 ? (
                      <Select
                        value={pageForm.table_name}
                        onValueChange={(value) => setPageForm({ ...pageForm, table_name: value })}
                      >
                        <SelectTrigger className="mt-2 bg-background border-border text-foreground" data-testid="table-select">
                          <SelectValue placeholder="Select a table" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border max-h-64">
                          {schema.tables.map((table) => (
                            <SelectItem
                              key={table.id}
                              value={table.name}
                              className="text-foreground focus:bg-muted"
                            >
                              <div className="flex items-center gap-2">
                                <Table className="w-4 h-4 text-primary" />
                                {table.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        className="mt-2 bg-background border-border text-foreground focus:border-primary"
                        placeholder="Table name"
                        value={pageForm.table_name}
                        onChange={(e) => setPageForm({ ...pageForm, table_name: e.target.value })}
                      />
                    )}
                  </div>
                  
                  {/* Airtable View Selector */}
                  {pageForm.table_name && (
                    <div>
                      <Label className="text-foreground/80">Airtable View (Optional)</Label>
                      <p className="text-xs text-muted-foreground mt-1 mb-2">
                        Filter records by an Airtable view. Selecting a view will also import its field visibility settings.
                      </p>
                      <Select
                        value={pageForm.airtable_view || "__all__"}
                        onValueChange={(value) => {
                          const newView = value === "__all__" ? "" : value;
                          setPageForm({ ...pageForm, airtable_view: newView, visible_fields: newView ? pageForm.visible_fields : null });
                          if (newView && pageForm.table_name) {
                            fetchViewMetadata(pageForm.table_name, newView);
                          }
                        }}
                        disabled={loadingViewMetadata}
                      >
                        <SelectTrigger className="bg-background border-border text-foreground" data-testid="view-select">
                          <SelectValue placeholder="All records (no view filter)" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border max-h-64">
                          <SelectItem value="__all__" className="text-foreground focus:bg-muted">
                            <div className="flex items-center gap-2">
                              <Eye className="w-4 h-4 text-muted-foreground" />
                              All records (no view filter)
                            </div>
                          </SelectItem>
                          {schema?.tables
                            ?.find(t => t.name === pageForm.table_name)
                            ?.views?.map((view) => (
                              <SelectItem
                                key={view.id}
                                value={view.name}
                                className="text-foreground focus:bg-muted"
                              >
                                <div className="flex items-center gap-2">
                                  <Eye className="w-4 h-4 text-primary" />
                                  {view.name}
                                </div>
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {loadingViewMetadata && (
                        <p className="text-xs text-primary mt-1 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Importing field settings from view...
                        </p>
                      )}
                      {pageForm.visible_fields && pageForm.visible_fields.length > 0 && (
                        <p className="text-xs text-green-500 mt-1">
                          ✓ {pageForm.visible_fields.length} fields will be visible (matching view settings)
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <Label className="text-foreground/80">Description</Label>
                    <Input
                      className="mt-2 bg-background border-border text-foreground focus:border-primary"
                      placeholder="Optional description"
                      value={pageForm.description}
                      onChange={(e) => setPageForm({ ...pageForm, description: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter className="mt-6">
                  <Button variant="outline" className="btn-secondary" onClick={() => setPageDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button className="btn-primary" onClick={handleSavePage} disabled={savingPage} data-testid="save-page-btn">
                    {savingPage ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingPage ? "Update" : "Add Page")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {pages.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No pages yet. Add your first page to get started.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pages.map((page, index) => (
                <div
                  key={page.page_id}
                  className="flex items-center gap-3 p-4 bg-background border border-border rounded-lg"
                  data-testid={`page-item-${page.page_id}`}
                >
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-foreground"
                      onClick={() => movePage(index, 'up')}
                      disabled={index === 0}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-foreground"
                      onClick={() => movePage(index, 'down')}
                      disabled={index === pages.length - 1}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="w-8 h-8 bg-secondary rounded flex items-center justify-center">
                    <FileText className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{page.nav_title || page.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      Table: {page.table_name}
                      {page.airtable_view && (
                        <span className="ml-2 text-primary">• View: {page.airtable_view}</span>
                      )}
                      {page.allowed_roles?.length > 0 && (
                        <span className="ml-2 text-amber-500">• Restricted: {page.allowed_roles.join(', ')}</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`${page.allow_create ? 'text-green-500' : 'text-muted-foreground'} hover:text-green-600`}
                      onClick={() => toggleAllowCreate(page)}
                      title={page.allow_create ? "Hide New Record button" : "Show New Record button"}
                      data-testid={`toggle-create-btn-${page.page_id}`}
                    >
                      <PlusCircle className="w-4 h-4" />
                    </Button>
                    {page.allow_create && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`${page.create_form_config?.visible_fields?.length > 0 ? 'text-green-500' : 'text-muted-foreground'} hover:text-green-600`}
                        onClick={() => openCreateFormConfig(page)}
                        title="Configure Create Form fields"
                        data-testid={`config-create-form-btn-${page.page_id}`}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => openFieldConfig(page)}
                      title="Configure fields"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`${page.allowed_roles?.length > 0 ? 'text-amber-500' : 'text-muted-foreground'} hover:text-amber-600`}
                      onClick={() => openRoleAccessDialog(page)}
                      title="Role access"
                      data-testid={`role-access-btn-${page.page_id}`}
                    >
                      <Shield className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => openEditPage(page)}
                      title="Edit page"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => handleDeletePage(page.page_id)}
                      title="Delete page"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Page Role Access Dialog */}
        <Dialog open={roleAccessDialogOpen} onOpenChange={setRoleAccessDialogOpen}>
          <DialogContent className="bg-card border-border text-foreground">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                Role Access - {selectedPageForRoles?.name}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Select which roles can access this page. Leave empty for all roles.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {getRoleOptions().length === 0 ? (
                <div className="text-center py-6">
                  <Shield className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    No roles configured. Set up Users & Authentication first to configure role-based access.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground mb-3">
                    {pageAllowedRoles.length === 0 
                      ? "All roles can currently access this page."
                      : `Only selected roles can access this page.`}
                  </p>
                  {getRoleOptions().map((role) => (
                    <div 
                      key={role}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        pageAllowedRoles.includes(role) 
                          ? 'bg-amber-500/10 border-amber-500' 
                          : 'bg-background border-border hover:border-muted-foreground'
                      }`}
                      onClick={() => toggleRoleAccess(role)}
                    >
                      <span className="font-medium text-foreground">{role}</span>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        pageAllowedRoles.includes(role) 
                          ? 'bg-amber-500 border-amber-500' 
                          : 'border-muted-foreground'
                      }`}>
                        {pageAllowedRoles.includes(role) && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" className="btn-secondary" onClick={() => setRoleAccessDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="btn-primary" 
                onClick={savePageRoleAccess}
                disabled={getRoleOptions().length === 0}
              >
                Save Access
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Form Configuration Dialog */}
        <Dialog open={createFormDialogOpen} onOpenChange={setCreateFormDialogOpen}>
          <DialogContent className="bg-card border-border text-foreground max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PlusCircle className="w-5 h-5 text-green-500" />
                Configure Create Form - {selectedPageForCreateForm?.name}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Choose which fields appear in the "New Record" form. Drag to reorder. Leave empty to show all fields.
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto py-4">
              {getCreateFormAvailableFields().length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No configurable fields found for this table</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground mb-4">
                    {createFormFields.length === 0 
                      ? "All fields will be shown (default behavior)" 
                      : `${createFormFields.length} field${createFormFields.length !== 1 ? 's' : ''} selected`}
                  </p>
                  
                  {/* Selected fields (draggable) */}
                  {createFormFieldOrder.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Selected Fields (drag to reorder)</p>
                      <div className="space-y-1 border border-border rounded-lg p-2 bg-background/50">
                        {createFormFieldOrder.map((fieldName, index) => {
                          const field = getCreateFormAvailableFields().find(f => f.name === fieldName);
                          if (!field) return null;
                          const isRequired = createFormFieldSettings[fieldName]?.required;
                          return (
                            <div
                              key={fieldName}
                              draggable
                              onDragStart={(e) => handleCreateFormDragStart(e, index)}
                              onDragOver={(e) => handleCreateFormDragOver(e, index)}
                              onDrop={(e) => handleCreateFormDrop(e, index)}
                              onDragEnd={() => { setCreateFormDraggedField(null); setCreateFormDragOverIndex(null); }}
                              className={`flex items-center gap-2 p-2 rounded-md cursor-move transition-colors ${
                                createFormDragOverIndex === index ? 'bg-primary/20 border-primary border' : 'bg-card hover:bg-muted/50'
                              }`}
                            >
                              <GripVertical className="w-4 h-4 text-muted-foreground" />
                              <Checkbox
                                checked={true}
                                onCheckedChange={() => toggleCreateFormField(fieldName)}
                                className="border-green-500 data-[state=checked]:bg-green-500"
                              />
                              <span className="flex-1 text-sm text-foreground">
                                {field.name}
                                {isRequired && <span className="text-red-500 ml-1">*</span>}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleCreateFormRequired(fieldName); }}
                                className={`text-xs px-2 py-1 rounded transition-colors ${
                                  isRequired 
                                    ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30' 
                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                }`}
                                title={isRequired ? "Click to make optional" : "Click to make required"}
                              >
                                {isRequired ? 'Required' : 'Optional'}
                              </button>
                              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">{field.type}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Available fields */}
                  <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Available Fields</p>
                  <div className="space-y-1">
                    {getCreateFormAvailableFields()
                      .filter(f => !createFormFields.includes(f.name))
                      .map((field) => (
                        <div
                          key={field.id}
                          className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors"
                        >
                          <div className="w-4" /> {/* Spacer for alignment */}
                          <Checkbox
                            checked={false}
                            onCheckedChange={() => toggleCreateFormField(field.name)}
                            className="border-border"
                          />
                          <span className="flex-1 text-sm text-foreground">{field.name}</span>
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">{field.type}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter className="border-t border-border pt-4">
              <Button
                variant="outline"
                className="btn-secondary"
                onClick={() => setCreateFormDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="btn-primary"
                onClick={saveCreateFormConfig}
              >
                <Save className="w-4 h-4 mr-2" />
                Save Configuration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Field Configuration Dialog - New Design */}
        <Dialog open={fieldDialogOpen} onOpenChange={(open) => {
          setFieldDialogOpen(open);
          if (!open) setFieldSearchTerm(""); // Reset search on close
        }}>
          <DialogContent className="bg-card border-border text-foreground max-w-6xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                Field Configuration - {selectedPageForFields?.name}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Choose which fields to display and customize their order. Drag rows to reorder.
              </DialogDescription>
            </DialogHeader>
            
            {/* Search & Actions Bar */}
            <div className="flex items-center justify-between gap-4 py-3 border-b border-border">
              <div className="relative flex-1 max-w-sm">
                <Input
                  className="bg-background border-border text-foreground pl-8"
                  placeholder="Search fields..."
                  value={fieldSearchTerm}
                  onChange={(e) => setFieldSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="text-xs text-muted-foreground">
                  <span className={`font-medium ${getFilterableCount() >= 8 ? 'text-amber-500' : 'text-primary'}`}>
                    {getFilterableCount()}/8
                  </span> filters
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <GripVertical className="w-4 h-4" />
                  <span>Drag to reorder</span>
                </div>
              </div>
            </div>

            {/* Field List Header */}
            <div className="grid gap-2 py-2 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider border-b border-border" style={{ gridTemplateColumns: '120px 1fr 80px 80px 80px 120px' }}>
              <div className="text-center">Order</div>
              <div>Field</div>
              <div className="text-center">
                <span>Visible</span>
                <div className="flex justify-center gap-1 mt-1 normal-case text-[10px]" style={{ position: 'relative', zIndex: 10 }}>
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      showAllFields();
                    }}
                    className="text-primary hover:underline cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                  >
                    All
                  </button>
                  <span className="text-muted-foreground/50">|</span>
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      hideAllFields();
                    }}
                    className="text-muted-foreground hover:text-foreground hover:underline cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                  >
                    None
                  </button>
                </div>
              </div>
              <div className="text-center">
                <span>Editable</span>
                <div className="flex justify-center gap-1 mt-1 normal-case text-[10px]" style={{ position: 'relative', zIndex: 10 }}>
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      makeAllEditable();
                    }}
                    className="text-yellow-500 hover:underline cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                  >
                    All
                  </button>
                  <span className="text-muted-foreground/50">|</span>
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      makeNoneEditable();
                    }}
                    className="text-muted-foreground hover:text-foreground hover:underline cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                  >
                    None
                  </button>
                </div>
              </div>
              <div className="text-center">Filter</div>
              <div className="text-center">Options</div>
            </div>

            {/* Field List */}
            <div className="flex-1 overflow-y-auto" key={`field-list-${selectedPageForFields?.visible_fields?.length ?? 'all'}`}>
              {filteredFieldOrder.map((fieldName) => {
                const actualIndex = fieldOrder.indexOf(fieldName); // Get actual index for reordering
                // Read visibility directly from state to ensure fresh values
                const visibleFields = selectedPageForFields?.visible_fields;
                const visible = visibleFields === null || visibleFields === undefined 
                  ? true 
                  : (visibleFields.length === 0 ? false : visibleFields.includes(fieldName));
                const fieldSchema = selectedPageForFields ? getFieldSchema(selectedPageForFields.table_name, fieldName) : null;
                const fieldType = fieldSchema?.type || 'text';
                const isLinkedRecord = fieldType === 'multipleRecordLinks';
                const linkedTableId = fieldSchema?.options?.linkedTableId;
                const isReadonly = isAutoReadonlyField(fieldType);
                const allowCreateLinked = fieldConfig[fieldName]?.allow_create_linked || false;
                const allowEdit = isReadonly ? false : (fieldConfig[fieldName]?.allow_edit || false);
                const allowExpand = fieldConfig[fieldName]?.allow_expand || false;
                const allowCreateView = fieldConfig[fieldName]?.allow_create_view || false;
                const hasLinkedConfig = !!(fieldConfig[fieldName]?.expand_config || fieldConfig[fieldName]?.create_config);
                const isDragging = draggedField?.name === fieldName;
                const isDragOver = dragOverIndex === actualIndex;
                
                return (
                  <div
                    key={`${fieldName}-${visible}`}
                    tabIndex={0}
                    draggable={!fieldSearchTerm} // Disable drag when searching
                    onDragStart={(e) => handleDragStart(e, fieldName, actualIndex)}
                    onDragOver={(e) => handleDragOver(e, actualIndex)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, actualIndex)}
                    onDragEnd={handleDragEnd}
                    onKeyDown={(e) => handleFieldKeyDown(e, actualIndex)}
                    onFocus={() => setSelectedFieldIndex(actualIndex)}
                    className={`grid gap-2 items-center py-3 px-2 border-b border-border/50 hover:bg-muted/30 transition-colors outline-none focus:ring-2 focus:ring-primary/50 ${
                      !visible ? 'opacity-50' : ''
                    } ${isDragging ? 'opacity-50 bg-muted/50' : ''} ${isDragOver ? 'border-t-2 border-t-primary' : ''} ${selectedFieldIndex === actualIndex ? 'bg-muted/20' : ''}`}
                    style={{ gridTemplateColumns: '120px 1fr 80px 80px 80px 120px' }}
                  >
                    {/* Drag Handle & Quick Move */}
                    <div className="flex items-center gap-2">
                      {!fieldSearchTerm && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => moveFieldToTop(actualIndex)}
                            disabled={actualIndex === 0}
                            title="Move to top"
                          >
                            <ChevronsUp className="w-3 h-3" />
                          </Button>
                          <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab active:cursor-grabbing flex-shrink-0" />
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => moveFieldToBottom(actualIndex)}
                            disabled={actualIndex === fieldOrder.length - 1}
                            title="Move to bottom"
                          >
                            <ChevronsDown className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Field Name */}
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${isLinkedRecord ? 'bg-blue-500/10' : isReadonly ? 'bg-muted' : 'bg-secondary/50'}`}>
                        {isLinkedRecord ? (
                          <LinkIcon className="w-4 h-4 text-blue-500" />
                        ) : isReadonly ? (
                          <EyeOff className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <FileText className="w-4 h-4 text-primary" />
                        )}
                      </div>
                      <span className="font-medium text-foreground truncate" title={fieldName}>{fieldName}</span>
                    </div>

                    {/* Visibility Toggle */}
                    <div className="flex items-center justify-center">
                      <Switch
                        checked={visible}
                        onCheckedChange={() => toggleFieldVisibility(fieldName)}
                        className="data-[state=checked]:bg-primary"
                      />
                    </div>

                    {/* Editable Toggle */}
                    <div className="flex items-center justify-center">
                      {isReadonly ? (
                        <span className="text-xs text-muted-foreground">Auto</span>
                      ) : (
                        <Switch
                          checked={allowEdit}
                          onCheckedChange={() => toggleAllowEdit(fieldName)}
                          className="data-[state=checked]:bg-yellow-500"
                        />
                      )}
                    </div>

                    {/* Filter Toggle */}
                    <div className="flex items-center justify-center">
                      {isFilterableFieldType(fieldType) ? (
                        <Switch
                          checked={fieldConfig[fieldName]?.is_filterable || false}
                          onCheckedChange={() => toggleFilterable(fieldName)}
                          className="data-[state=checked]:bg-cyan-500"
                          data-testid={`filter-toggle-${fieldName}`}
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </div>

                    {/* Options - Linked Record Settings */}
                    <div className="flex items-center justify-center">
                      {isLinkedRecord ? (
                        <div className="flex flex-col gap-1.5">
                          {/* Expandable toggle */}
                          <div className="flex items-center gap-1.5">
                            <Switch
                              checked={allowExpand}
                              onCheckedChange={() => toggleAllowExpand(fieldName)}
                              className="data-[state=checked]:bg-purple-500 scale-75"
                            />
                            <span className={`text-[10px] ${allowExpand ? 'text-purple-500' : 'text-muted-foreground'}`}>
                              Expand
                            </span>
                          </div>
                          {/* +CREATE toggle */}
                          <div className="flex items-center gap-1.5">
                            <Switch
                              checked={allowCreateView}
                              onCheckedChange={() => toggleAllowCreateView(fieldName)}
                              className="data-[state=checked]:bg-green-500 scale-75"
                            />
                            <span className={`text-[10px] ${allowCreateView ? 'text-green-500' : 'text-muted-foreground'}`}>
                              +Create
                            </span>
                          </div>
                          {/* Configure button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-6 px-2 text-[10px] ${hasLinkedConfig ? 'text-blue-500 hover:text-blue-600' : 'text-muted-foreground hover:text-foreground'}`}
                            onClick={() => openLinkedFieldConfig(fieldName, linkedTableId)}
                            data-testid={`configure-linked-${fieldName}`}
                          >
                            Configure
                            <ChevronRight className="w-3 h-3 ml-0.5" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {filteredFieldOrder.length === 0 && fieldSearchTerm && (
                <div className="py-8 text-center text-muted-foreground">
                  No fields matching "{fieldSearchTerm}"
                </div>
              )}
              {fieldOrder.length === 0 && (
                <div className="py-8 text-center text-muted-foreground">
                  No fields found for this table
                </div>
              )}
            </div>

            <DialogFooter className="border-t border-border pt-4">
              <Button variant="outline" className="btn-secondary" onClick={() => setFieldDialogOpen(false)}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Linked Field Configuration Dialog */}
        <Dialog open={linkedConfigDialogOpen} onOpenChange={setLinkedConfigDialogOpen}>
          <DialogContent className="bg-card border-border text-foreground max-w-5xl w-[90vw] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="border-b border-border pb-4">
              <DialogTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <LinkIcon className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <span className="text-lg">Configure: {linkedConfigFieldName}</span>
                  <p className="text-sm font-normal text-muted-foreground mt-1">
                    {getLinkedTableById(linkedConfigTableId)?.name || 'Linked Table'} Fields
                  </p>
                </div>
              </DialogTitle>
            </DialogHeader>
            
            {/* Tab Navigation */}
            <div className="flex gap-2 py-3 border-b border-border">
              <Button
                variant={linkedConfigTab === 'preview' ? 'default' : 'ghost'}
                size="sm"
                className={linkedConfigTab === 'preview' ? 'bg-blue-500 hover:bg-blue-600' : ''}
                onClick={() => setLinkedConfigTab('preview')}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Cards
              </Button>
              <Button
                variant={linkedConfigTab === 'expand' ? 'default' : 'ghost'}
                size="sm"
                className={linkedConfigTab === 'expand' ? 'bg-purple-500 hover:bg-purple-600' : ''}
                onClick={() => setLinkedConfigTab('expand')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Expand View
              </Button>
              <Button
                variant={linkedConfigTab === 'create' ? 'default' : 'ghost'}
                size="sm"
                className={linkedConfigTab === 'create' ? 'bg-green-500 hover:bg-green-600' : ''}
                onClick={() => setLinkedConfigTab('create')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Form
              </Button>
            </div>

            {/* Preview Cards Tab Content */}
            {linkedConfigTab === 'preview' && (
              <div className="flex-1 overflow-y-auto py-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Select up to 4 fields to show in preview cards. Drag to reorder.
                </p>
                
                {/* Selected Preview Fields */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-foreground mb-2">Selected Fields ({linkedPreviewFields.length}/4)</h4>
                  <div className="space-y-2 min-h-[100px] p-3 bg-muted/30 rounded-lg border border-border">
                    {linkedPreviewFields.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No fields selected. Click fields below to add.</p>
                    ) : (
                      linkedPreviewFields.map((fieldName, index) => (
                        <div
                          key={fieldName}
                          className="flex items-center gap-2 p-2 bg-card rounded-md border border-border"
                        >
                          <div className="flex flex-col gap-0.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={() => movePreviewField(index, 'up')}
                              disabled={index === 0}
                            >
                              <ChevronUp className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={() => movePreviewField(index, 'down')}
                              disabled={index === linkedPreviewFields.length - 1}
                            >
                              <ChevronDown className="w-3 h-3" />
                            </Button>
                          </div>
                          <span className="flex-1 font-medium text-foreground">{fieldName}</span>
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">#{index + 1}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive hover:text-destructive"
                            onClick={() => togglePreviewField(fieldName)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Available Fields to Add */}
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Available Fields</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {getLinkedTableFields(linkedConfigTableId)
                      .filter(f => !linkedPreviewFields.includes(f.name))
                      .map((field) => (
                        <button
                          key={field.name}
                          className={`flex items-center gap-2 p-2 rounded-md border text-left transition-colors ${
                            linkedPreviewFields.length >= 4
                              ? 'border-border/50 text-muted-foreground cursor-not-allowed opacity-50'
                              : 'border-border hover:bg-muted/50 text-foreground'
                          }`}
                          onClick={() => togglePreviewField(field.name)}
                          disabled={linkedPreviewFields.length >= 4}
                        >
                          <Plus className="w-4 h-4 text-primary" />
                          <span className="text-sm truncate">{field.name}</span>
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* Expand/Create Tab Content */}
            {(linkedConfigTab === 'expand' || linkedConfigTab === 'create') && (
              <>
            {/* Search */}
            <div className="py-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search fields..."
                  value={linkedFieldSearchTerm}
                  onChange={(e) => setLinkedFieldSearchTerm(e.target.value)}
                  className="pl-9 bg-background border-border"
                />
              </div>
            </div>

            {/* Field List Header */}
            <div className={`grid gap-2 py-2 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider border-b border-border ${linkedConfigTab === 'create' ? 'grid-cols-12' : 'grid-cols-12'}`}>
              <div className="col-span-2 text-center">Order</div>
              <div className={linkedConfigTab === 'create' ? 'col-span-2' : 'col-span-4'}>Field</div>
              <div className="col-span-2 text-center" style={{ overflow: 'visible' }}>
                <span>Visible</span>
                <div className="flex justify-center gap-1 mt-1 normal-case text-[10px]" style={{ position: 'relative', zIndex: 10 }}>
                  <button 
                    type="button" 
                    onClick={(e) => { 
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('showAllLinkedFields clicked'); 
                      showAllLinkedFields(); 
                    }} 
                    className="text-primary hover:underline cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                  >
                    All
                  </button>
                  <span className="text-muted-foreground/50">|</span>
                  <button 
                    type="button" 
                    onClick={(e) => { 
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('hideAllLinkedFields clicked'); 
                      hideAllLinkedFields(); 
                    }} 
                    className="text-muted-foreground hover:text-foreground hover:underline cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                  >
                    None
                  </button>
                </div>
              </div>
              {linkedConfigTab === 'create' && (
                <div className="col-span-2 text-center">Required</div>
              )}
              <div className="col-span-2 text-center" style={{ overflow: 'visible' }}>
                <span>Editable</span>
                <div className="flex justify-center gap-1 mt-1 normal-case text-[10px]" style={{ position: 'relative', zIndex: 10 }}>
                  <button 
                    type="button" 
                    onClick={(e) => { 
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('makeAllLinkedEditable clicked'); 
                      makeAllLinkedEditable(); 
                    }} 
                    className="text-yellow-500 hover:underline cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                  >
                    All
                  </button>
                  <span className="text-muted-foreground/50">|</span>
                  <button 
                    type="button" 
                    onClick={(e) => { 
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('makeNoneLinkedEditable clicked'); 
                      makeNoneLinkedEditable(); 
                    }} 
                    className="text-muted-foreground hover:text-foreground hover:underline cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                  >
                    None
                  </button>
                </div>
              </div>
              <div className="col-span-2 text-center">Type</div>
            </div>

            {/* Field List */}
            <div className="flex-1 overflow-y-auto min-h-[200px]">
              {getFilteredLinkedFieldOrder().map((fieldName, displayIndex) => {
                const config = linkedConfigTab === 'expand' ? linkedExpandConfig : linkedCreateConfig;
                const actualIndex = config.field_order.indexOf(fieldName);
                const linkedFields = getLinkedTableFields(linkedConfigTableId);
                const fieldSchema = linkedFields.find(f => f.name === fieldName);
                const fieldType = fieldSchema?.type || 'text';
                const isLinkedRecordField = fieldType === 'multipleRecordLinks';
                const isReadonly = isAutoReadonlyField(fieldType);
                // Read visibility directly from config state to ensure fresh values
                const visible = config.visible_fields?.includes(fieldName) ?? true;
                const editable = config.field_settings?.[fieldName]?.allow_edit ?? false;
                const required = linkedConfigTab === 'create' ? (config.field_settings?.[fieldName]?.required ?? false) : false;
                const isDragging = linkedDraggedField?.name === fieldName;
                const isDragOver = linkedDragOverIndex === actualIndex;

                return (
                  <div
                    key={`${fieldName}-${visible}-${editable}`}
                    draggable={!linkedFieldSearchTerm}
                    onDragStart={(e) => handleLinkedDragStart(e, fieldName, actualIndex)}
                    onDragOver={(e) => handleLinkedDragOver(e, actualIndex)}
                    onDragLeave={handleLinkedDragLeave}
                    onDrop={(e) => handleLinkedDrop(e, actualIndex)}
                    onDragEnd={handleLinkedDragEnd}
                    className={`grid grid-cols-12 gap-2 items-center py-3 px-2 border-b border-border/50 hover:bg-muted/30 transition-colors ${
                      !visible ? 'opacity-50' : ''
                    } ${isDragging ? 'opacity-50 bg-muted/50' : ''} ${isDragOver ? 'border-t-2 border-t-primary' : ''}`}
                  >
                    {/* Drag Handle & Reorder */}
                    <div className="col-span-2 flex items-center gap-1">
                      {!linkedFieldSearchTerm && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => moveLinkedFieldToTop(actualIndex)}
                            disabled={actualIndex === 0}
                            title="Move to top"
                          >
                            <ChevronsUp className="w-3 h-3" />
                          </Button>
                          <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab active:cursor-grabbing flex-shrink-0" />
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => moveLinkedFieldToBottom(actualIndex)}
                            disabled={actualIndex === config.field_order.length - 1}
                            title="Move to bottom"
                          >
                            <ChevronsDown className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Field Name */}
                    <div className={`${linkedConfigTab === 'create' ? 'col-span-2' : 'col-span-4'} flex items-center gap-3`}>
                      <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${isLinkedRecordField ? 'bg-blue-500/10' : isReadonly ? 'bg-muted' : 'bg-secondary/50'}`}>
                        {isLinkedRecordField ? (
                          <LinkIcon className="w-4 h-4 text-blue-500" />
                        ) : isReadonly ? (
                          <EyeOff className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <FileText className="w-4 h-4 text-primary" />
                        )}
                      </div>
                      <span className="font-medium text-foreground truncate" title={fieldName}>{fieldName}</span>
                    </div>

                    {/* Visibility Toggle */}
                    <div className="col-span-2 flex items-center justify-center">
                      <Switch
                        checked={visible}
                        onCheckedChange={() => toggleLinkedFieldVisibility(fieldName)}
                        className="data-[state=checked]:bg-primary"
                      />
                    </div>

                    {/* Required Toggle (only for Create Form) */}
                    {linkedConfigTab === 'create' && (
                      <div className="col-span-2 flex items-center justify-center">
                        {isReadonly ? (
                          <span className="text-xs text-muted-foreground">-</span>
                        ) : (
                          <Switch
                            checked={required}
                            onCheckedChange={() => toggleLinkedFieldRequired(fieldName)}
                            className="data-[state=checked]:bg-red-500"
                          />
                        )}
                      </div>
                    )}

                    {/* Editable Toggle */}
                    <div className="col-span-2 flex items-center justify-center">
                      {isReadonly ? (
                        <span className="text-xs text-muted-foreground">Auto</span>
                      ) : (
                        <Switch
                          checked={editable}
                          onCheckedChange={() => toggleLinkedFieldEditable(fieldName)}
                          className="data-[state=checked]:bg-yellow-500"
                        />
                      )}
                    </div>

                    {/* Field Type (no nested configure - 1 layer limit) */}
                    <div className="col-span-2 flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">
                        {isLinkedRecordField ? 'Link' : isReadonly ? 'Auto' : '-'}
                      </span>
                    </div>
                  </div>
                );
              })}
              {getFilteredLinkedFieldOrder().length === 0 && linkedFieldSearchTerm && (
                <div className="py-8 text-center text-muted-foreground">
                  No fields matching "{linkedFieldSearchTerm}"
                </div>
              )}
            </div>
              </>
            )}

            <DialogFooter className="border-t border-border pt-4 flex justify-between">
              <Button variant="outline" className="btn-secondary" onClick={() => setLinkedConfigDialogOpen(false)}>
                Cancel
              </Button>
              <Button className="btn-primary" onClick={saveLinkedFieldConfig}>
                <Save className="w-4 h-4 mr-2" />
                Save Configuration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Users & Authentication */}
        <section className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Users & Authentication</h2>
              <p className="text-sm text-muted-foreground">Configure portal users, roles, and authentication methods</p>
            </div>
          </div>
          
          {/* Sub-tabs */}
          <div className="flex items-center gap-4 mb-6 border-b border-border">
            <button
              className={`flex items-center gap-2 pb-3 px-1 border-b-2 transition-colors ${
                userSettingsTab === 'users' 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setUserSettingsTab('users')}
              data-testid="users-roles-tab"
            >
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">Users & Roles</span>
            </button>
            <button
              className={`flex items-center gap-2 pb-3 px-1 border-b-2 transition-colors ${
                userSettingsTab === 'auth' 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setUserSettingsTab('auth')}
              data-testid="authentication-tab"
            >
              <Lock className="w-4 h-4" />
              <span className="text-sm font-medium">Authentication</span>
            </button>
          </div>
          
          {/* Users & Roles Tab */}
          {userSettingsTab === 'users' && (
            <div className="space-y-6">
              <div>
                <Label className="text-foreground/80 flex items-center gap-1">
                  Users Table <span className="text-red-500">*</span>
                </Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  This table will contain all users who can access your portal
                </p>
                <Select
                  value={userSettings.users_table || "__none__"}
                  onValueChange={(value) => setUserSettings({ 
                    ...userSettings, 
                    users_table: value === "__none__" ? "" : value,
                    role_field: "", // Reset dependent fields
                    email_field: "",
                    password_field: ""
                  })}
                >
                  <SelectTrigger className="bg-background border-border text-foreground" data-testid="users-table-select">
                    <SelectValue placeholder="Select a table" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-64">
                    <SelectItem value="__none__" className="text-muted-foreground">
                      No table selected
                    </SelectItem>
                    {schema?.tables?.map((table) => (
                      <SelectItem
                        key={table.id}
                        value={table.name}
                        className="text-foreground focus:bg-muted"
                      >
                        <div className="flex items-center gap-2">
                          <Table className="w-4 h-4 text-primary" />
                          {table.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {userSettings.users_table && (
                <div>
                  <Label className="text-foreground/80">Role Field (Single Select Only)</Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Add a single-select field to your Users table with the role options you need. These roles control each user's permissions.
                  </p>
                  <Select
                    value={userSettings.role_field || "__none__"}
                    onValueChange={(value) => setUserSettings({ 
                      ...userSettings, 
                      role_field: value === "__none__" ? "" : value 
                    })}
                  >
                    <SelectTrigger className="bg-background border-border text-foreground" data-testid="role-field-select">
                      <SelectValue placeholder="Select a role field" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-64">
                      <SelectItem value="__none__" className="text-muted-foreground">
                        No role field (all users same access)
                      </SelectItem>
                      {getSingleSelectFields(userSettings.users_table).map((field) => (
                        <SelectItem
                          key={field.id}
                          value={field.name}
                          className="text-foreground focus:bg-muted"
                        >
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-amber-500" />
                            {field.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {getSingleSelectFields(userSettings.users_table).length === 0 && (
                    <p className="text-xs text-amber-500 mt-2">
                      No single-select fields found in {userSettings.users_table}. Create one in Airtable for roles.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Authentication Tab */}
          {userSettingsTab === 'auth' && (
            <div className="space-y-6">
              {/* Email & Password Auth Option */}
              <div className="p-4 bg-background rounded-lg border border-border">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-foreground">Email & Password</h3>
                      <Switch
                        checked={userSettings.auth_method === 'email_password'}
                        onCheckedChange={(checked) => setUserSettings({ 
                          ...userSettings, 
                          auth_method: checked ? 'email_password' : '' 
                        })}
                        className="data-[state=checked]:bg-green-500"
                        data-testid="email-password-toggle"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Standard login using both email and password.
                    </p>
                    
                    {userSettings.auth_method === 'email_password' && userSettings.users_table && (
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                        <div>
                          <Label className="text-foreground/80">Email Field</Label>
                          <p className="text-xs text-muted-foreground mt-1 mb-2">
                            Select the field that contains your user's email addresses.
                          </p>
                          <Select
                            value={userSettings.email_field || "__none__"}
                            onValueChange={(value) => setUserSettings({ 
                              ...userSettings, 
                              email_field: value === "__none__" ? "" : value 
                            })}
                          >
                            <SelectTrigger className="bg-card border-border text-foreground" data-testid="email-field-select">
                              <SelectValue placeholder="Select email field" />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border max-h-64">
                              <SelectItem value="__none__" className="text-muted-foreground">
                                Select a field
                              </SelectItem>
                              {getTableFieldsByName(userSettings.users_table).map((field) => (
                                <SelectItem
                                  key={field.id}
                                  value={field.name}
                                  className="text-foreground focus:bg-muted"
                                >
                                  {field.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-foreground/80">Password Field</Label>
                          <p className="text-xs text-muted-foreground mt-1 mb-2">
                            Select the field that contains your user's passwords.
                          </p>
                          <Select
                            value={userSettings.password_field || "__none__"}
                            onValueChange={(value) => setUserSettings({ 
                              ...userSettings, 
                              password_field: value === "__none__" ? "" : value 
                            })}
                          >
                            <SelectTrigger className="bg-card border-border text-foreground" data-testid="password-field-select">
                              <SelectValue placeholder="Select password field" />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border max-h-64">
                              <SelectItem value="__none__" className="text-muted-foreground">
                                Select a field
                              </SelectItem>
                              {getTableFieldsByName(userSettings.users_table).map((field) => (
                                <SelectItem
                                  key={field.id}
                                  value={field.name}
                                  className="text-foreground focus:bg-muted"
                                >
                                  {field.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                    
                    {userSettings.auth_method === 'email_password' && !userSettings.users_table && (
                      <p className="text-xs text-amber-500 mt-2">
                        Select a Users Table in the "Users & Roles" tab first.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Save Button */}
              <div className="flex justify-end">
                <Button 
                  className="btn-primary" 
                  onClick={handleSaveUserSettings}
                  disabled={savingUserSettings}
                  data-testid="save-user-settings-btn"
                >
                  {savingUserSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save User Settings
                </Button>
              </div>
            </div>
          )}
          
          {/* Save Button for Users & Roles Tab */}
          {userSettingsTab === 'users' && (
            <div className="flex justify-end mt-6">
              <Button 
                className="btn-primary" 
                onClick={handleSaveUserSettings}
                disabled={savingUserSettings}
                data-testid="save-user-settings-btn-users"
              >
                {savingUserSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save User Settings
              </Button>
            </div>
          )}
        </section>
        
        {/* Visibility & Access */}
        <section className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
              <LinkIcon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Visibility & Access</h2>
              <p className="text-sm text-muted-foreground">Control who can access this portal</p>
            </div>
          </div>
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
              <div>
                <h3 className="font-medium text-foreground">Public Access</h3>
                <p className="text-sm text-muted-foreground">Anyone with the link can view this portal</p>
              </div>
              <Switch
                checked={portal?.is_public || false}
                onCheckedChange={(checked) => setPortal({ ...portal, is_public: checked })}
                data-testid="public-access-switch"
              />
            </div>
            {portal?.is_public && (
              <div className="flex items-center gap-2">
                <Input
                  className="flex-1 bg-background border-border text-muted-foreground"
                  value={`${window.location.origin}/p/${portal?.share_token}`}
                  readOnly
                />
                <Button variant="outline" className="btn-secondary" onClick={copyPublicLink} data-testid="copy-public-link-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Branding */}
        <section className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
              <Palette className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Branding</h2>
              <p className="text-sm text-muted-foreground">Customize the look of your portal</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-foreground/80">Company Name</Label>
              <Input
                className="mt-2 bg-background border-border text-foreground focus:border-primary"
                value={portal?.branding?.company_name || ""}
                onChange={(e) => setPortal({
                  ...portal,
                  branding: { ...portal.branding, company_name: e.target.value }
                })}
                data-testid="company-name-input"
              />
            </div>
            <div>
              <Label className="text-foreground/80">Primary Color</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="color"
                  className="w-12 h-10 p-1 bg-background border-border cursor-pointer"
                  value={portal?.branding?.primary_color || "#2CC295"}
                  onChange={(e) => setPortal({
                    ...portal,
                    branding: { ...portal.branding, primary_color: e.target.value }
                  })}
                />
                <Input
                  className="flex-1 bg-background border-border text-foreground focus:border-primary"
                  value={portal?.branding?.primary_color || "#2CC295"}
                  onChange={(e) => setPortal({
                    ...portal,
                    branding: { ...portal.branding, primary_color: e.target.value }
                  })}
                  data-testid="primary-color-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-foreground/80">Logo URL</Label>
              <Input
                className="mt-2 bg-background border-border text-foreground focus:border-primary"
                placeholder="https://example.com/logo.png"
                value={portal?.branding?.logo_url || ""}
                onChange={(e) => setPortal({
                  ...portal,
                  branding: { ...portal.branding, logo_url: e.target.value }
                })}
                data-testid="logo-url-input"
              />
            </div>
          </div>
        </section>

        {/* Client Access */}
        <section className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">Client Access</h2>
                <p className="text-sm text-muted-foreground">Manage individual client access</p>
              </div>
            </div>
            <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-client-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Invite Client
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border text-foreground">
                <DialogHeader>
                  <DialogTitle>Invite Client</DialogTitle>
                  <DialogDescription className="text-muted-foreground">
                    Send an email invitation to a new client. They'll receive a link to create their password.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-foreground/80">Client Email *</Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="email"
                        className="pl-10 bg-background border-border text-foreground focus:border-primary"
                        placeholder="client@example.com"
                        value={newClientEmail}
                        onChange={(e) => setNewClientEmail(e.target.value)}
                        data-testid="client-email-input"
                      />
                    </div>
                  </div>
                  
                  {/* Role dropdown - only show if user settings are configured */}
                  {userSettings.users_table && userSettings.role_field && (
                    <div>
                      <Label className="text-foreground/80">Assign Role</Label>
                      <Select
                        value={newClientRole || ""}
                        onValueChange={(value) => setNewClientRole(value)}
                      >
                        <SelectTrigger className="mt-2 bg-background border-border" data-testid="client-role-select">
                          <SelectValue placeholder="Select a role (optional)" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="">No specific role</SelectItem>
                          {getRoleOptions().map(role => (
                            <SelectItem key={role} value={role}>{role}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Determines which pages this client can access
                      </p>
                    </div>
                  )}
                </div>
                <DialogFooter className="mt-6">
                  <Button variant="outline" className="btn-secondary" onClick={() => setClientDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    className="btn-primary"
                    onClick={handleAddClient}
                    disabled={addingClient || !newClientEmail}
                    data-testid="add-client-submit"
                  >
                    {addingClient ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Invitation"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          
          {clients.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No clients added yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {clients.map((client) => (
                <div
                  key={client.access_id}
                  className="flex items-center justify-between p-4 bg-background rounded-lg border border-border"
                  data-testid={`client-${client.access_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                      <Mail className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-foreground">{client.client_email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-primary"
                      onClick={() => copyClientLink(client.access_token)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveClient(client.access_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pending Invites Section */}
          {invites.filter(i => i.status === 'pending').length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Pending Invites</h3>
              <div className="space-y-2">
                {invites.filter(i => i.status === 'pending').map((invite) => (
                  <div
                    key={invite.invite_id}
                    className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg border border-amber-500/30"
                    data-testid={`invite-${invite.invite_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center">
                        <Mail className="w-4 h-4 text-amber-500" />
                      </div>
                      <div>
                        <span className="text-foreground">{invite.email}</span>
                        {invite.role && (
                          <span className="ml-2 text-xs bg-amber-500/20 text-amber-600 px-2 py-0.5 rounded">
                            {invite.role}
                          </span>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Sent: {new Date(invite.created_at).toLocaleDateString()} • Expires: {new Date(invite.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-primary"
                        onClick={() => handleResendInvite(invite.invite_id, invite.email)}
                        title="Resend invite"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteInvite(invite.invite_id)}
                        title="Cancel invite"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Accepted Invites Section */}
          {invites.filter(i => i.status === 'accepted').length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Accepted Invites</h3>
              <div className="space-y-2">
                {invites.filter(i => i.status === 'accepted').map((invite) => (
                  <div
                    key={invite.invite_id}
                    className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30"
                    data-testid={`invite-accepted-${invite.invite_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                        <UserCheck className="w-4 h-4 text-green-500" />
                      </div>
                      <div>
                        <span className="text-foreground">{invite.email}</span>
                        {invite.role && (
                          <span className="ml-2 text-xs bg-green-500/20 text-green-600 px-2 py-0.5 rounded">
                            {invite.role}
                          </span>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Accepted: {invite.accepted_at ? new Date(invite.accepted_at).toLocaleDateString() : 'Unknown'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
